import { useEffect, useState } from "react"
import { motion } from "framer-motion"
import { X, User, Users, Repeat } from "lucide-react"
import {
  createAppointment,
  createRecurringAppointments,
  getAvailableClients,
  checkAvailability,
} from "../../../api/appointments"
import { getInitials } from "../../../utils/getInitials"
import DateTimeFields from "./DateTimeFields"
import type { Appointment, Client, RecurrenceFrequency } from "../../../types"

// Format date as YYYY-MM-DD using local timezone (avoids UTC shift from toISOString)
function localDateStr(d: Date): string {
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, "0")
  const day = String(d.getDate()).padStart(2, "0")
  return `${y}-${m}-${day}`
}

export default function AddAppointmentModal({
  onClose,
  onCreated,
  onCreatedMany,
  defaultDate,
}: {
  onClose: () => void
  onCreated: (a: Appointment) => void
  onCreatedMany?: (skipped: string[]) => void
  defaultDate?: Date
}) {
  const [appointmentType, setAppointmentType] = useState<"personal" | "group_class">("personal")
  const [date, setDate] = useState(localDateStr(defaultDate ?? new Date()))
  const [startTime, setStartTime] = useState("08:00")
  const [endTime, setEndTime] = useState("09:00")
  const [notes, setNotes] = useState("")
  const [location, setLocation] = useState("")
  const [selectedIds, setSelectedIds] = useState<number[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [recurrence, setRecurrence] = useState<RecurrenceFrequency>("none")
  const [recurrenceCount, setRecurrenceCount] = useState(4)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [prevType, setPrevType] = useState(appointmentType)
  if (prevType !== appointmentType) {
    setPrevType(appointmentType)
    setSelectedIds([])
  }

  // Fetch clients filtered by the appointment type (personal vs group_class)
  useEffect(() => {
    let cancelled = false
    getAvailableClients(appointmentType)
      .then((data) => {
        if (!cancelled) setClients(data)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [appointmentType])

  function toggleClient(id: number) {
    setSelectedIds((prev) => {
      if (prev.includes(id)) return prev.filter((x) => x !== id)
      if (appointmentType === "personal" && prev.length >= 1) return [id]
      if (appointmentType === "group_class" && prev.length >= 4) return prev
      return [...prev, id]
    })
  }

  // Auto-generate title from selected participant names
  const autoTitle =
    selectedIds
      .map((id) => clients.find((c) => c.id === id)?.name)
      .filter(Boolean)
      .join(", ") || "Cita"

  const isValid = date && startTime && endTime && endTime > startTime && selectedIds.length > 0

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!isValid) return
    setSaving(true)
    setError(null)
    try {
      const payload = {
        title: autoTitle,
        appointment_type: appointmentType,
        start_time: `${date}T${startTime}:00`,
        end_time: `${date}T${endTime}:00`,
        notes: notes || undefined,
        location: location || undefined,
        participant_ids: selectedIds,
      }

      // Recurring series: the backend validates capacity per occurrence and
      // skips the ones without room, so we don't pre-check availability here.
      if (recurrence !== "none") {
        const { skipped } = await createRecurringAppointments(payload, {
          frequency: recurrence,
          count: recurrenceCount,
        })
        onCreatedMany?.(skipped)
        return
      }

      // Single appointment: check availability for each hour in the range first.
      const startH = parseInt(startTime.split(":")[0], 10)
      const endH = parseInt(endTime.split(":")[0], 10)
      for (let h = startH; h < endH; h++) {
        const { available } = await checkAvailability({ date, hour: h })
        if (!available) {
          setError("No hay campo para este horario, seleccione otro")
          setSaving(false)
          return
        }
      }
      const created = await createAppointment(payload)
      onCreated(created)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { errors?: string[] } } })?.response?.data
        ?.errors?.[0]
      setError(msg ?? "Error al crear la cita.")
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <motion.div
        initial={{ opacity: 0, scale: 0.95 }}
        animate={{ opacity: 1, scale: 1 }}
        className="w-full max-w-md rounded-2xl border border-white/10 gradient-card p-6 shadow-2xl max-h-[90vh] overflow-y-auto"
      >
        {/* Header */}
        <div className="mb-5 flex items-center justify-between">
          <div>
            <h2 className="text-lg font-semibold text-white">Nueva Cita</h2>
            <p className="text-xs text-muted">Agendar entrenamiento</p>
          </div>
          <button
            onClick={onClose}
            className="text-muted hover:text-white transition text-xl leading-none"
          >
            <X size={18} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Type toggle */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">Tipo</label>
            <div className="grid grid-cols-2 gap-2">
              <button
                type="button"
                onClick={() => setAppointmentType("personal")}
                className={`flex items-center justify-center gap-2 rounded-lg border py-2 text-sm font-medium transition ${
                  appointmentType === "personal"
                    ? "border-primary bg-primary/10 text-primary"
                    : "border-white/10 text-muted hover:text-white"
                }`}
              >
                <User size={14} />
                Personal
              </button>
              <button
                type="button"
                onClick={() => setAppointmentType("group_class")}
                className={`flex items-center justify-center gap-2 rounded-lg border py-2 text-sm font-medium transition ${
                  appointmentType === "group_class"
                    ? "border-amber-400 bg-amber-400/10 text-amber-400"
                    : "border-white/10 text-muted hover:text-white"
                }`}
              >
                <Users size={14} />
                Grupal
              </button>
            </div>
          </div>

          {/* Date + Times */}
          <DateTimeFields
            date={date}
            setDate={setDate}
            startTime={startTime}
            setStartTime={setStartTime}
            endTime={endTime}
            setEndTime={setEndTime}
          />

          {/* Recurrence */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 flex items-center gap-1.5">
              <Repeat size={12} /> Repetir
            </label>
            <div className="flex gap-2">
              <select
                value={recurrence}
                onChange={(e) => setRecurrence(e.target.value as RecurrenceFrequency)}
                className="flex-1 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-primary focus:outline-none"
              >
                <option value="none">No se repite</option>
                <option value="daily">Diariamente</option>
                <option value="weekly">Semanalmente</option>
                <option value="monthly">Mensualmente</option>
              </select>
              {recurrence !== "none" && (
                <div className="flex items-center gap-1.5">
                  <input
                    type="number"
                    min={1}
                    max={52}
                    value={recurrenceCount}
                    onChange={(e) =>
                      setRecurrenceCount(Math.min(52, Math.max(1, Number(e.target.value) || 1)))
                    }
                    className="w-16 rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white focus:border-primary focus:outline-none"
                  />
                  <span className="text-xs text-muted">veces</span>
                </div>
              )}
            </div>
            {recurrence !== "none" && (
              <p className="mt-1 text-[11px] text-muted">
                Las ocurrencias sin cupo se omitirán automáticamente.
              </p>
            )}
          </div>

          {/* Participants */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">
              Participantes {appointmentType === "personal" ? "(máx 1)" : "(máx 4)"}
            </label>
            {appointmentType === "group_class" && selectedIds.length === 4 && (
              <p className="text-xs text-amber-400 mb-1">Capacidad máxima alcanzada</p>
            )}
            <div className="max-h-36 overflow-y-auto space-y-1 rounded-lg border border-white/10 bg-white/[0.02] p-2">
              {clients.length === 0 ? (
                <p className="text-xs text-muted py-2 text-center">No hay clientes disponibles</p>
              ) : (
                clients.map((c) => (
                  <button
                    key={c.id}
                    type="button"
                    onClick={() => toggleClient(c.id)}
                    className={`w-full flex items-center gap-2 rounded-lg px-3 py-1.5 text-sm transition ${
                      selectedIds.includes(c.id)
                        ? "bg-primary/10 border border-primary/30 text-primary"
                        : "text-muted hover:text-white hover:bg-white/5"
                    }`}
                  >
                    <span className="inline-flex h-6 w-6 items-center justify-center rounded-full bg-white/10 text-[10px] font-bold">
                      {getInitials(c.name)}
                    </span>
                    {c.name}
                  </button>
                ))
              )}
            </div>
          </div>

          {/* Location */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">
              Ubicación (opcional)
            </label>
            <input
              type="text"
              value={location}
              onChange={(e) => setLocation(e.target.value)}
              placeholder="Ej: Sala 1, Área de pesas..."
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-primary focus:outline-none"
            />
          </div>

          {/* Notes */}
          <div>
            <label className="text-xs font-medium text-muted mb-1.5 block">Notas (opcional)</label>
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={2}
              placeholder="Instrucciones o notas..."
              className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder-white/30 focus:border-primary focus:outline-none resize-none"
            />
          </div>

          {/* Error */}
          {error && (
            <p className="rounded-lg border border-danger/30 bg-danger/10 px-3 py-2 text-xs text-danger">
              {error}
            </p>
          )}

          {/* Actions */}
          <div className="flex gap-2 pt-1">
            <button
              type="submit"
              disabled={saving || !isValid}
              className="flex-1 rounded-lg bg-primary py-2.5 text-sm font-semibold text-black hover:opacity-90 transition disabled:opacity-50"
            >
              {saving ? "Creando..." : "Crear Cita"}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded-lg border border-white/10 px-4 py-2.5 text-sm text-muted hover:text-white transition"
            >
              Cancelar
            </button>
          </div>
        </form>
      </motion.div>
    </div>
  )
}
